import { CalendarIcon, ClockIcon, UserIcon } from "@xola/icons";
import { Checkbox, flash, Modal, SubmitButton } from "@xola/ui-kit";
import { isEquipmentItem, PurchaseActionOption } from "common";
import dayjs from "dayjs";
import { isNil } from "lodash";
import React, { ChangeEvent, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import { Experience, ExperiencePurchaseItem, Permission, Purchase, PurchaseItem, PurchaseItemPrivacy } from "types";
import { ProductImage } from "../Common/ProductImage";
import { SecondaryButton } from "../Common/SecondaryButton";
import { RefundOption } from "../Dashboard/RefundOption";
import { AddOnLimitOverrideSwitch } from "./AddOnLimitOverrideSwitch";
import { getNextPurchaseModificationActions } from "./helpers/getNextPurchaseModificationActions";
import { PaymentBreakdown } from "./PaymentBreakdown";
import { PurchaseItemSelect } from "./PurchaseItemSelect";
import { api } from "@/api/api";
import { useSeller } from "@/hooks/useAccount";
import { OverrideCatalogItemRestrictions, useAddOnLimitOverride } from "@/hooks/useAddOnLimitOverride";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useResource } from "@/hooks/useResource";
import { isAllowedPrivacy } from "@/models/PurchaseItem";
import { updateCache } from "@/utils/updateCache";

interface EditPurchasePrivacyModalProps {
    isOpen: boolean;
    onClose: (
        modalOption: PurchaseActionOption | null,
        refundOption: RefundOption | null,
        refundAmount: number,
    ) => void;
    purchase: Purchase;
    item: ExperiencePurchaseItem;
}

interface PurchasePayload {
    purchase: Purchase;
    item: ExperiencePurchaseItem;
    status: PurchaseItemPrivacy;
}

export const EditPurchasePrivacyModal = (props: EditPurchasePrivacyModalProps) => {
    return (
        <Modal size="huge" isOpen={props.isOpen} onClose={props.onClose}>
            <ModalContent {...props} />
        </Modal>
    );
};

const ModalContent = ({ onClose, purchase, item }: Except<EditPurchasePrivacyModalProps, "isOpen">) => {
    const { isGranted } = useAuthorization();

    const [notifyGuests, setNotifyGuests] = useState<boolean>(true);
    const addOnLimitOverride = useAddOnLimitOverride();
    const [selectedItem, setSelectedItem] = useState<ExperiencePurchaseItem>(item);
    const { data: experience } = useResource<Experience>(["experiences", selectedItem.product.id]);
    const { mutateAsync: applyBookingPrivateOrShare, isLoading } = useMutation(
        async ({ purchase, item, status }: PurchasePayload) => {
            const payload = getPayload({ item, status, itemOverride: addOnLimitOverride.itemOverride });
            const { data } = await api.patch<Purchase>(`/purchases/${purchase.id}`, payload);
            return data;
        },
    );
    // Memoized because the preview runs again whenever the payload object changes.
    const previewPayload = useMemo(() => {
        return getPayload({ item: selectedItem, status: selectedItem.privacy });
    }, [selectedItem]);

    const isPrivacyUnchanged =
        isAllowedPrivacy(selectedItem.privacy) && selectedItem.privacy === experience?.allowedPrivacies?.[0];

    const handleItemChange = (item: PurchaseItem) => {
        addOnLimitOverride.reset();
        setSelectedItem(item as ExperiencePurchaseItem);
    };

    const handleApply = async () => {
        try {
            const updatedPurchase = await applyBookingPrivateOrShare({
                purchase,
                item: selectedItem,
                status: selectedItem.privacy,
            });

            const isChanged = purchase.balance !== updatedPurchase.balance;
            const refundAmount = updatedPurchase?.balance < 0 ? updatedPurchase?.balance : 0;
            const hasCustomerEmail = purchase.customerEmail !== "";
            const { nextModalOption, nextRefundOption } = getNextPurchaseModificationActions({
                balance: updatedPurchase?.balance ?? 0,
                hasPurchaseChanges: isChanged,
                hasCustomerEmail,
                canViewInvoice: isGranted(Permission.INVOICE_VIEW),
                refundAmount,
            });

            updateCache("purchases", updatedPurchase);
            onClose(nextModalOption, nextRefundOption, refundAmount);
        } catch (error: any) {
            if (addOnLimitOverride.handleError(error)) {
                return;
            }

            const { response } = error;

            const eventConflict = response?.status === 409 ? response?.data?.field?.event : undefined;
            if (eventConflict) {
                flash.danger(eventConflict.message);
            } else {
                flash.danger("Something went wrong");
            }
        }
    };

    return (
        <fieldset disabled={isLoading}>
            <Modal.Header>{getTitle(selectedItem.privacy)}</Modal.Header>
            <Modal.Body className="flex-col space-y-4">
                {purchase.items.length > 1 ? (
                    <PurchaseItemSelect
                        items={purchase.items}
                        value={selectedItem}
                        className="flex-1"
                        onChange={handleItemChange}
                    />
                ) : null}
                <div className="flex space-x-4">
                    <div className="flex-1 space-y-4">
                        <div className="rounded-lg bg-gray-lighter p-6">
                            <div className="mb-3 text-lg font-bold">
                                {getFromItemTitleForPrivacy(selectedItem.privacy)}
                            </div>
                            <PurchaseItemBanner purchaseItem={selectedItem} />
                            <PaymentBreakdown
                                showTitles={false}
                                isEditable={false}
                                purchase={{ ...purchase }}
                                item={selectedItem}
                            />
                        </div>
                    </div>
                    <div className="flex-1" data-testid="updated-purchase-summary">
                        <div className="p-6">
                            <div className="mb-3 text-lg font-bold">
                                {getToItemTitleForPrivacy(selectedItem.privacy)}
                            </div>
                            <PurchaseItemBanner purchaseItem={selectedItem} />
                            <PaymentBreakdown
                                showTitles={false}
                                isEditable={false}
                                purchase={{ ...purchase }}
                                item={selectedItem}
                                payload={previewPayload}
                            />
                        </div>
                    </div>
                </div>
                <AddOnLimitOverrideSwitch limitOverride={addOnLimitOverride} />
            </Modal.Body>
            <Modal.Footer className="flex justify-end">
                <Checkbox
                    checked={notifyGuests}
                    label="Notify Guests"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setNotifyGuests(e.target.checked);
                    }}
                />
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <SubmitButton
                    isLoading={isLoading}
                    disabled={
                        isNil(experience?.allowedPrivacies) ||
                        (experience?.allowedPrivacies.length === 1 && isPrivacyUnchanged) ||
                        addOnLimitOverride.isAddonLimitExceeded
                    }
                    onClick={handleApply}
                >
                    Apply
                </SubmitButton>
            </Modal.Footer>
        </fieldset>
    );
};

const getTitle = (privacy: PurchaseItemPrivacy) => {
    if (privacy === PurchaseItemPrivacy.PUBLIC) {
        return "Make Booking Private";
    }

    return "Make Booking Shared";
};

const getFromItemTitleForPrivacy = (privacy: PurchaseItemPrivacy) => {
    if (privacy === PurchaseItemPrivacy.PUBLIC) {
        return "From Shared Experience";
    }

    return "From Private Experience";
};

const getToItemTitleForPrivacy = (privacy: PurchaseItemPrivacy) => {
    if (privacy === PurchaseItemPrivacy.PUBLIC) {
        return "To Private Experience";
    }

    return "To Shared Experience";
};

interface PurchaseItemBannerProps {
    purchaseItem: ExperiencePurchaseItem;
}

const PurchaseItemBanner = ({ purchaseItem }: PurchaseItemBannerProps) => {
    const seller = useSeller();
    const arrivalDateTime = dayjs(purchaseItem.arrivalDateTime).tz(seller.timezoneName);
    const { data: experience } = useResource<Experience>(["products", purchaseItem.product.id]);

    return (
        <div className="ml-[-3px] flex w-full items-center">
            <ProductImage product={purchaseItem.product} size="medium" />

            <div className="w-full flex-1">
                <div className="text-base font-bold text-black" data-testid="purchase-item-summary-tile-experience">
                    {experience?.name}
                </div>
                <div className="flex space-x-3 text-gray-darker">
                    <div data-testid="purchase-item-summary-tile-guests-count">
                        <UserIcon className="mr-0.5" />
                        {purchaseItem.quantity} {isEquipmentItem(purchaseItem) ? "Units" : "Guests"}
                    </div>
                    <span>
                        <CalendarIcon className="left-[-1.5px]" />
                        <span data-testid="purchase-item-summary-tile-arrival-date">
                            {arrivalDateTime.format("ddd ll")}
                        </span>
                    </span>
                </div>
                <div className="text-gray-darker">
                    {purchaseItem.arrivalTime ? (
                        <span data-testid="purchase-item-summary-tile-arrival-time">
                            <ClockIcon className="mr-0.5" />
                            {arrivalDateTime.format("LT")}
                        </span>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

interface PrivacyPayloadParams {
    item: ExperiencePurchaseItem;
    status: PurchaseItemPrivacy;
    itemOverride?: OverrideCatalogItemRestrictions;
}

const getPayload = ({ item, status, itemOverride = {} }: PrivacyPayloadParams) => {
    return {
        items: [
            {
                id: item.id,
                privacy:
                    status === PurchaseItemPrivacy.PUBLIC ? PurchaseItemPrivacy.PRIVATE : PurchaseItemPrivacy.PUBLIC,
                ...itemOverride,
            },
        ],
    };
};
